















































































































































import { Vue, Prop, Component } from 'vue-property-decorator';
import { BackgroundTypes, Retailer } from '@/models/BookMeetingFlow';
import MeetingTypeItem from './MeetingTypeItem.vue';
import NewMeetingBookingFlow from './NewMeetingBookingFlow.vue';
import { logMeetingSolution, meetingLog, meetingWarn } from '@/services/MeetingBookingLog';

@Component({
    components: {
        MeetingTypeItem,
        NewMeetingBookingFlow,
    }
})
class SelectedItem extends Vue {
    [x: string]: any;
    @Prop() retailer!: Retailer;
    @Prop() itemWrapperClass: string | undefined;
    @Prop({ type: Boolean, default: false }) initView!: boolean;
    @Prop({ type: Boolean, default: false }) useNewMeetingBooking!: boolean;
    @Prop({ type: Object, default: null }) backgroundTypes!: BackgroundTypes;
    @Prop() currentCulture!: string;
    @Prop({ type: String, default: '' }) bookingSource!: string;

    selectedMeetingType: string | null = null;
    // Set by the button that opened the modal: open-meeting-modal can carry { detail: { source } }.
    openedFromSource: string | null = null;

    get effectiveBookingSource(): string {
        return this.openedFromSource || this.bookingSource;
    }

    isTypeAvailable(type: string): boolean {
        const types = this.retailer.availableMeetingTypes;
        return !!types && types.indexOf(type) !== -1;
    }

    /**
     * Decides whether a meeting type is offered. The two solutions are kept strictly apart: a
     * retailer on the new one is driven by availableMeetingTypes alone, and the old booking urls
     * never get a say — not even as a fallback when the new solution reports nothing.
     */
    showMeetingType(type: 'store' | 'home' | 'video'): boolean {
        if (this.useNewMeetingBooking) {
            return !!this.retailer.organizationSystemId && this.isTypeAvailable(type);
        }

        const urls: Record<string, string | null> = {
            store: this.retailer.bookMeetingUrlStore,
            home: this.retailer.bookMeetingUrlHome,
            video: this.retailer.bookMeetingUrlVideo
        };
        return !!urls[type];
    }

    get hasAnyMeetingType(): boolean {
        return this.showMeetingType('store') || this.showMeetingType('home') || this.showMeetingType('video');
    }

    private logSolution(): void {
        const types = this.retailer.availableMeetingTypes;

        logMeetingSolution('SelectedItem', {
            useNewMeetingBooking: this.useNewMeetingBooking,
            source: 'use-new-meeting-booking prop from the page/block',
            retailerName: this.retailer.name,
            organizationSystemId: this.retailer.organizationSystemId,
            availableMeetingTypes: types,
            bookMeetingUrlStore: this.retailer.bookMeetingUrlStore,
            bookMeetingUrlHome: this.retailer.bookMeetingUrlHome,
            bookMeetingUrlVideo: this.retailer.bookMeetingUrlVideo,
            initView: this.initView,
            shownMeetingTypes: {
                store: this.showMeetingType('store'),
                home: this.showMeetingType('home'),
                video: this.showMeetingType('video')
            },
            hasAnyMeetingType: this.hasAnyMeetingType
        });

        if (this.useNewMeetingBooking && !this.retailer.organizationSystemId) {
            meetingWarn('SelectedItem',
                'new booking is on but organizationSystemId is missing — the api cannot resolve the store, no meeting types will be offered');
        }

        if (this.useNewMeetingBooking && (!types || types.length === 0)) {
            meetingWarn('SelectedItem',
                'new booking is on but availableMeetingTypes is empty — no meeting type can be offered. ' +
                'Check that the meeting booking api lists this store with times.');
        }
    }

    mounted() {
        this.matomoEvent('booking_flow', 'Store_select', this.retailer.name);
        this.logSolution();

        if (this.initView) {
            window.addEventListener('open-meeting-modal', this.openModal);
        }
    }

    beforeDestroy() {
        window.removeEventListener('open-meeting-modal', this.openModal);
    }

    openModal(event?: Event): void {
        const detail = event && (event as CustomEvent).detail;
        this.openedFromSource = detail && detail.source ? String(detail.source) : null;
        this.selectedMeetingType = null;
        this.$modal.show('direct-meeting-modal');
    }

    closeModal(): void {
        this.$modal.hide('direct-meeting-modal');
    }

    trackMeetingClick(type: string): void {
        this.matomoEvent('booking_flow', 'Meeting_type', type);
    }

    handleModalBack(): void {
        const flow = this.$refs.bookingFlow as any;
        if (flow && typeof flow.handleBack === 'function') {
            flow.handleBack();
        } else {
            this.selectedMeetingType = null;
        }
    }

    selectMeetingType(type: string): void {
        if (this.initView && this.useNewMeetingBooking) {
            // Stay in the modal and show the booking flow
            meetingLog('SelectedItem', 'meeting type "' + type + '" opens the NEW booking flow in the modal', {
                retailer: this.retailer.name,
                organizationSystemId: this.retailer.organizationSystemId
            });
            this.selectedMeetingType = type;
        } else {
            meetingLog('SelectedItem', 'meeting type "' + type + '" handled by the OLD flow (link/parent)', {
                retailer: this.retailer.name,
                initView: this.initView,
                useNewMeetingBooking: this.useNewMeetingBooking,
                url: type === 'store' ? this.retailer.bookMeetingUrlStore
                    : type === 'home' ? this.retailer.bookMeetingUrlHome
                    : this.retailer.bookMeetingUrlVideo
            });
            // Old system: close the modal (links open in new tab via <a>)
            // Block flow: emit to parent (MeetingRetailerModal handles it)
            if (this.initView) {
                this.closeModal();
            }
            this.$emit('meeting-type-selected', type);
        }
    }
}

export default SelectedItem;
